import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, FileText, Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { normalizeEvidenceUrl } from "@/lib/capExampleEvidence";
import { getEvidenceUrl } from "@/lib/evidenceAccess";

const sb: any = supabase as any;

interface EvidenceItem {
  id: string;
  evidence_type: string;
  file_url?: string | null;
  link_url?: string | null;
  title?: string | null;
  description?: string | null;
  uploaded_by_role?: string | null;
  created_at: string;
  source: "direct" | "curated";
}
interface EvidenceRequest {
  id: string;
  description: string;
  due_date: string | null;
  status: "open" | "fulfilled" | "cancelled";
  requested_by: string;
  created_at: string;
  fulfilled_at: string | null;
}
interface Payload {
  finding_id: string;
  direct: EvidenceItem[];
  curated: EvidenceItem[];
  requests: EvidenceRequest[];
}

interface Props {
  findingId: string;
  /** Deprecated — request workflow lives in Conversation tab. Retained for API compatibility. */
  canRequest?: boolean;
  /** Deprecated — fulfillment lives in Conversation tab. Retained for API compatibility. */
  canFulfill?: boolean;
  /** Refresh signal from parent. */
  reloadKey?: number;
  /** Allow removing directly-attached evidence (respondent's own uploads/links). */
  canRemove?: boolean;
  /** Called after a successful removal. */
  onChanged?: () => void;
}

export const UnifiedEvidence: React.FC<Props> = ({ findingId, reloadKey, canRemove = false, onChanged }) => {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: d, error } = await sb.rpc("get_finding_evidence_unified", {
      p_finding_id: findingId,
    });
    if (error) {
      toast.error(error.message ?? "Failed to load evidence");
      setData(null);
    } else {
      setData(d as Payload);
    }
    setLoading(false);
  }, [findingId]);

  const handleRemove = useCallback(
    async (id: string) => {
      setRemovingId(id);
      const { error } = await sb.rpc("respondent_remove_action_evidence_link", {
        p_evidence_id: id,
      });
      setRemovingId(null);
      if (error) {
        toast.error(error.message ?? "Failed to remove evidence");
        return;
      }
      toast.success("Evidence removed");
      await load();
      onChanged?.();
    },
    [load, onChanged],
  );


  useEffect(() => {
    load();
  }, [load, reloadKey]);


  if (loading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading evidence…
      </div>
    );
  }
  if (!data) return null;

  const allItems = [...data.direct, ...data.curated];
  const openRequests = data.requests.filter((r) => r.status === "open");
  const otherRequests = data.requests.filter((r) => r.status !== "open");

  return (
    <div className="space-y-4">
      {/* Submitted Evidence */}
      <section>
        <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
          Submitted Evidence
          <Badge variant="secondary" className="text-[10px]">{allItems.length}</Badge>
        </h4>
        {allItems.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No evidence has been submitted yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {allItems.map((e) => {
              return (
                <li
                  key={`${e.source}-${e.id}`}
                  className="flex items-center justify-between gap-2 text-xs rounded border bg-card px-2.5 py-1.5"
                >
                  <EvidenceAnchor item={e} />
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge variant="outline" className="text-[10px]">{e.source}</Badge>
                    {e.uploaded_by_role && (
                      <span className="text-[10px] text-muted-foreground">{e.uploaded_by_role}</span>
                    )}
                    {canRemove && e.source === "direct" && (
                      <button
                        type="button"
                        aria-label="Remove evidence"
                        title="Remove this evidence"
                        disabled={removingId === e.id}
                        onClick={() => handleRemove(e.id)}
                        className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      >
                        {removingId === e.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </button>
                    )}
                  </div>

                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Requested Evidence — read-only audit roll-up; managed in Conversation. */}
      <section>
        <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          Requested Evidence
          <Badge variant="secondary" className="text-[10px]">{data.requests.length}</Badge>
        </h4>
        {openRequests.length === 0 && otherRequests.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No evidence requests. Reviewers can request evidence from the Conversation section.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {openRequests.map((r) => (
              <li key={r.id} className="rounded border bg-amber-500/5 border-amber-500/30 px-2.5 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs min-w-0">
                    <div className="font-medium truncate">{r.description}</div>
                    <div className="text-[10px] text-muted-foreground">
                      Requested {new Date(r.created_at).toLocaleDateString()}
                      {r.due_date && ` · Due ${new Date(r.due_date).toLocaleDateString()}`}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">Open</Badge>
                </div>
              </li>
            ))}
            {otherRequests.map((r) => (
              <li key={r.id} className="rounded border bg-muted/30 px-2.5 py-1.5">
                <div className="text-xs flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-muted-foreground line-through truncate">{r.description}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {r.status === "fulfilled" ? "Fulfilled" : "Cancelled"}
                      {r.fulfilled_at && ` · ${new Date(r.fulfilled_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{r.status}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

function EvidenceAnchor({ item }: { item: EvidenceItem }) {
  const [href, setHref] = useState<string | null>(() => normalizeEvidenceUrl(item.link_url || null));
  const label = item.title || item.link_url || item.file_url?.split("/").pop() || "Evidence";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (item.link_url) {
        setHref(normalizeEvidenceUrl(item.link_url));
        return;
      }
      if (!item.file_url) {
        setHref(null);
        return;
      }
      try {
        // ACTION 2: server-brokered access by evidence record ID.
        const signed = await getEvidenceUrl({ evidenceId: item.id });

        if (!cancelled) setHref(signed);
      } catch (error) {
        console.error("[UnifiedEvidence] signed URL failed", error);
        if (!cancelled) setHref(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [item.file_url, item.link_url]);

  if (!href) {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground truncate min-w-0">
        <FileText className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      download={item.file_url ? label : undefined}
      className="flex items-center gap-1.5 text-primary hover:underline truncate min-w-0"
    >
      <ExternalLink className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </a>
  );
}

export default UnifiedEvidence;
